'use client';

import { useEffect } from 'react';
import { trackPhoneClick } from '@/lib/analytics/leadTracking';

/**
 * Where on the page a tel: link sits: an explicit data-track wins, otherwise
 * the surrounding region (header / footer / fixed sticky bar / page body).
 */
function phoneLinkLocation(a: HTMLAnchorElement): string {
  const tagged = a.closest('[data-track]')?.getAttribute('data-track');
  if (tagged) return tagged;
  if (a.closest('header, nav')) return 'header';
  if (a.closest('footer')) return 'footer';
  for (let el: HTMLElement | null = a; el && el !== document.body; el = el.parentElement) {
    if (getComputedStyle(el).position === 'fixed') return 'sticky';
  }
  return 'page_body';
}

/**
 * Site-wide: a tap on any tel: link (header, footer, pages, the call bar)
 * fires the phone-click lead events once per tap (GA4 phone_click, Google Ads
 * "Phone click" conversion, Meta Contact). Capture phase, so it still runs if
 * a component stops propagation. The link itself is never altered or blocked.
 * A second tap on the same link within 2s (double tap / ghost click) is not
 * counted again.
 */
export default function PhoneClickTracker() {
  useEffect(() => {
    let last = { href: '', at: 0 };
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.('a[href^="tel:"]') as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute('href') || '';
      const now = Date.now();
      if (href === last.href && now - last.at < 2000) return;
      last = { href, at: now };
      try {
        trackPhoneClick(phoneLinkLocation(a), window.location.pathname);
      } catch { /* tracking must never interfere with the call */ }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);
  return null;
}
